"use client";

import { useEffect, useRef, useState } from "react";

// ---- Latency comparison: Photon vs other VLMs (lower is better) ----
const BENCH = [
  { label: "Moondream 3.1 + Photon", ms: 59, note: "H100 · batch 1", emphasis: true },
  { label: "Qwen 3.5 4B + vLLM", ms: 73, note: "H100 · batch 1", emphasis: false },
  { label: "GPT-5.4 Mini", ms: 2780, note: "OpenAI API", emphasis: false },
  { label: "Gemini 2.5 Flash", ms: 3790, note: "Google API", emphasis: false },
];

const AXIS_BREAK_START_MS = 500;
const AXIS_BREAK_END_MS = 2500;
// The skipped 0.5s–2.5s span is compressed into a narrow zone of the track
// instead of a single point, so an animating bar crawls visibly through the
// axis break rather than appearing to stall on it.
const BREAK_ZONE_START = 44;
const BREAK_ZONE_END = 52;

const getBrokenAxisPercent = (ms: number, maxMs: number) => {
  if (ms <= AXIS_BREAK_START_MS) {
    return (ms / AXIS_BREAK_START_MS) * BREAK_ZONE_START;
  }

  if (ms < AXIS_BREAK_END_MS) {
    const inBreak =
      (ms - AXIS_BREAK_START_MS) / (AXIS_BREAK_END_MS - AXIS_BREAK_START_MS);
    return BREAK_ZONE_START + inBreak * (BREAK_ZONE_END - BREAK_ZONE_START);
  }

  const postBreakRange = maxMs - AXIS_BREAK_END_MS;
  if (postBreakRange <= 0) return BREAK_ZONE_END;

  const postBreakPercent = Math.max(0, ms - AXIS_BREAK_END_MS) / postBreakRange;
  return BREAK_ZONE_END + postBreakPercent * (100 - BREAK_ZONE_END);
};

export const BenchmarkChart = () => {
  const fastest = Math.min(...BENCH.map((b) => b.ms));
  const slowest = Math.max(...BENCH.map((b) => b.ms));
  const cardRef = useRef<HTMLDivElement | null>(null);
  const [started, setStarted] = useState(false);
  const [cycle, setCycle] = useState(0);
  const [filling, setFilling] = useState(false);
  const [elapsedMs, setElapsedMs] = useState(0);

  // Trigger first run when the card scrolls into view
  useEffect(() => {
    if (!cardRef.current) return;
    const el = cardRef.current;
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            setStarted(true);
            io.disconnect();
            break;
          }
        }
      },
      { threshold: 0.25 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  // Animation loop: snap-to-empty → fill (each bar takes its own `ms`) → hold 3s → repeat.
  // Bars start together; each finishes when its real-world latency would have elapsed,
  // so the fastest model "wins the race" first.
  useEffect(() => {
    if (!started) return;
    setFilling(false);
    setElapsedMs(0);
    // Wait one paint so the empty state commits before we kick off the transition.
    let raf = 0;
    let startTime = 0;
    const tick = (time: number) => {
      if (!startTime) startTime = time;
      const nextElapsed = Math.min(time - startTime, slowest);
      setElapsedMs(nextElapsed);
      if (nextElapsed < slowest) raf = requestAnimationFrame(tick);
    };
    const t1 = setTimeout(() => {
      setFilling(true);
      raf = requestAnimationFrame(tick);
    }, 50);
    // After the slowest bar finishes plus a 3s pause, restart the cycle.
    const t2 = setTimeout(() => setCycle((c) => c + 1), 50 + slowest + 3000);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      cancelAnimationFrame(raf);
    };
  }, [started, cycle, slowest]);

  return (
    <div className="bench-card" ref={cardRef}>
      <div className="bench-head">
        <div>
          <div className="t-eyebrow" style={{ color: "var(--text-tertiary)" }}>
            Internal benchmark
          </div>
          <div className="t-heading-md" style={{ marginTop: 4 }}>
            Time per request
          </div>
        </div>
        <div className="bench-controls">
          <span
            className="mono"
            style={{ fontSize: 11, color: "var(--text-tertiary)" }}
          >
            median of 200 runs · lower is better
          </span>
        </div>
      </div>
      <div className="bench-plot">
        <div className="bench-rows">
          {BENCH.map((b) => {
            const currentMs = filling ? Math.min(elapsedMs, b.ms) : 0;
            const pct = getBrokenAxisPercent(currentMs, slowest);
            const mult = b.ms / fastest;
            return (
              <div
                className={`bench-row ${b.emphasis ? "emph" : ""}`}
                key={b.label}
              >
                <div className="bench-label">
                  <span className="bench-name">{b.label}</span>
                  <span className="bench-note mono">{b.note}</span>
                </div>
                <div className="bench-bar-wrap">
                  <span
                    className="bench-axis-break"
                    aria-hidden="true"
                    title="Axis break: 0.5s to 2.5s skipped"
                  />
                  <div
                    className="bench-bar"
                    style={{
                      width: `${pct}%`,
                    }}
                  />
                </div>
                <div className="bench-value tabular mono">
                  <span className="bench-num">
                    {b.ms >= 1000 ? (b.ms / 1000).toFixed(2) : b.ms.toLocaleString()}
                  </span>
                  <span className="bench-unit">{b.ms >= 1000 ? " s" : " ms"}</span>
                  {!b.emphasis && (
                    <span className="bench-mult">
                      {mult >= 10 ? Math.round(mult) : mult.toFixed(1)}× slower
                    </span>
                  )}
                  {b.emphasis && (
                    <span
                      className="bench-mult"
                      style={{ color: "var(--accent-text)" }}
                    >
                      baseline
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
      <div className="bench-foot">
        <span
          className="mono"
          style={{ fontSize: 11, color: "var(--text-tertiary)" }}
        >
          direct-answer query · batch 1 · NVIDIA H100 80GB · 2026-02 build
        </span>
      </div>
    </div>
  );
};

// ---- Hardware range: Photon latency/throughput per tier ----
// Data: github.com/m87-labs/kestrel/blob/main/PERFORMANCE.md (direct query).
// Latency = P50 at batch 1; throughput = best sustained req/s across batches.
type HwRow = {
  name: string;
  tier: string;
  ms: number;
  rps: number;
  note?: string;
};

const HARDWARE: HwRow[] = [
  { name: "NVIDIA B200", tier: "Datacenter", ms: 49, rps: 77.7 },
  { name: "NVIDIA H100", tier: "Datacenter", ms: 59, rps: 58.0 },
  { name: "RTX PRO 6000", tier: "Workstation", ms: 68, rps: 38.8 },
  { name: "NVIDIA L40S", tier: "Server", ms: 99, rps: 24.7 },
  { name: "NVIDIA A100 80GB", tier: "Cloud", ms: 136, rps: 16.7 },
  { name: "Jetson AGX Thor", tier: "Edge", ms: 246, rps: 12.6 },
  { name: "NVIDIA A10", tier: "Cloud", ms: 280, rps: 7.2 },
  { name: "NVIDIA L4", tier: "Cloud", ms: 317, rps: 6.9 },
  { name: "Jetson AGX Orin", tier: "Edge", ms: 514, rps: 4.6, note: "Moondream 2" },
];

export const HardwareRange = () => {
  const maxRps = Math.max(...HARDWARE.map((h) => h.rps));
  return (
    <div className="hw-panel">
      <div className="hw-panel-head">
        <div className="t-eyebrow" style={{ color: "var(--text-tertiary)" }}>
          Photon · hardware
        </div>
        <div className="t-heading-md" style={{ marginTop: 4 }}>
          Same model, every tier.
        </div>
        <p
          className="t-body-md"
          style={{
            color: "var(--text-secondary)",
            marginTop: 8,
            maxWidth: 620,
          }}
        >
          Moondream 3.1 on Photon, benchmarked from datacenter to Jetson.
          Latency is the median for a single direct-answer query; throughput
          is peak sustained requests per second.
        </p>
      </div>
      <div className="hw-table">
        <div className="hw-row hw-head mono" aria-hidden="true">
          <span>Hardware</span>
          <span className="hw-ms">P50 latency</span>
          <span className="hw-tp">Peak throughput</span>
        </div>
        {HARDWARE.map((h) => (
          <div className="hw-row" key={h.name}>
            <span className="hw-name">
              {h.name}
              <span className="hw-tier mono">
                {h.tier}
                {h.note ? ` · ${h.note}` : ""}
              </span>
            </span>
            <span className="hw-ms mono tabular">
              {h.ms} <em>ms</em>
            </span>
            <span className="hw-tp">
              <span className="hw-bar" aria-hidden="true">
                <span style={{ width: `${(h.rps / maxRps) * 100}%` }} />
              </span>
              <span className="hw-rps mono tabular">
                {h.rps.toFixed(1)} <em>req/s</em>
              </span>
            </span>
          </div>
        ))}
      </div>
      <div className="hw-foot mono">
        ChartQA test split · prefix caching enabled · Jetson AGX Orin runs
        Moondream 2 ·{" "}
        <a
          href="https://github.com/m87-labs/kestrel/blob/main/PERFORMANCE.md"
          target="_blank"
          rel="noreferrer"
        >
          full data on GitHub
        </a>
      </div>
    </div>
  );
};

export const PartnerClouds = () => {
  const partners: { name: string; status: "live" | "soon" }[] = [
    { name: "FAL", status: "live" },
    { name: "Self-hosted", status: "live" },
    { name: "Moondream Cloud", status: "live" },
  ];
  return (
    <div className="partners-row">
      {partners.map((p) => (
        <div className={`partner-chip ${p.status}`} key={p.name}>
          <span className="partner-dot" />
          <span className="partner-name">{p.name}</span>
          {p.status === "soon" && (
            <span className="partner-soon">coming</span>
          )}
        </div>
      ))}
    </div>
  );
};
